import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Dumbbell, User as UserIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import client from '../../api/client';
import { exerciseName } from '../../lib/exerciseDisplay';

const DIFFICULTY_COLORS = {
  Beginner: 'bg-green-500/15 text-green-600 border-green-500/30',
  Intermediate: 'bg-yellow-500/15 text-yellow-600 border-yellow-500/30',
  Advanced: 'bg-red-500/15 text-red-600 border-red-500/30',
};

function formatSetsSummary(exercise) {
  const sets = Array.isArray(exercise?.sets) ? exercise.sets : [];
  if (sets.length === 0) return '—';
  const first = sets[0] || {};
  const min = first.target_reps_min;
  const max = first.target_reps_max;
  const reps = (min != null && max != null && min !== max)
    ? `${min}-${max}`
    : (min != null ? `${min}` : (exercise?.reps || '—'));
  return `${sets.length} × ${reps}`;
}

function RoutineRow({ routine }) {
  const [expanded, setExpanded] = useState(false);
  const days = Array.isArray(routine?.days) ? routine.days : [];

  return (
    <div className="border border-border/50 rounded-lg bg-muted/20" data-testid={`personal-routine-${routine.id}`}>
      <button
        type="button"
        className="w-full flex items-center justify-between p-3 text-left hover:bg-muted/40 active:bg-muted/60 transition-colors min-h-[44px]"
        onClick={() => setExpanded(v => !v)}
        data-testid={`personal-routine-toggle-${routine.id}`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Dumbbell className="h-4 w-4 text-primary shrink-0" />
          <span className="font-semibold text-sm truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {routine.name || '—'}
          </span>
          {routine.difficulty && (
            <Badge variant="outline" className={`text-[10px] ${DIFFICULTY_COLORS[routine.difficulty] || ''}`}>
              {routine.difficulty}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground ml-1 shrink-0">· {days.length} {days.length === 1 ? 'day' : 'days'}</span>
        </div>
        {expanded
          ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
          : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-border/50 p-3 space-y-3" data-testid={`personal-routine-body-${routine.id}`}>
          {days.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-2">No days defined.</p>
          ) : (
            days.map((day, di) => {
              const exs = Array.isArray(day?.exercises) ? day.exercises : [];
              return (
                <div key={day.id || di} className="space-y-1.5">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-foreground/80">
                    {day.day_name || day.name || `Day ${di + 1}`}
                  </h4>
                  {exs.length === 0 ? (
                    <p className="text-xs text-muted-foreground pl-2">— empty —</p>
                  ) : (
                    <ul className="space-y-0.5 pl-2">
                      {exs.map((ex, ei) => (
                        <li
                          key={ex.id || `${di}-${ei}`}
                          className="flex items-center justify-between gap-2 text-xs"
                        >
                          <span className="truncate text-foreground/90">{exerciseName(ex) || ex?.name || '—'}</span>
                          <span className="text-muted-foreground tabular-nums shrink-0">{formatSetsSummary(ex)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default function PersonalRoutinesPreview({ userId, userName }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['user-personal-routines', userId],
    queryFn: async () => {
      const res = await client.get(`/users/${userId}/personal-routines`);
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled: !!userId,
    staleTime: 30_000,
  });

  // Hide the card entirely on no-access, error, loading, or empty state.
  if (isLoading || isError || !Array.isArray(data) || data.length === 0) {
    return null;
  }

  return (
    <Card className="border-border/50" data-testid="personal-routines-preview-card">
      <CardHeader className="pb-3">
        <CardTitle
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          className="flex items-center justify-between"
        >
          <span className="flex items-center gap-2">
            <UserIcon className="h-5 w-5 text-primary" />
            {t('clientDetail.personalRoutinesTitle', { name: userName || '—' })}
          </span>
          <Badge variant="secondary" className="text-xs">
            {t('clientDetail.personalRoutinesCount', { count: data.length })}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="max-h-[min(500px,60dvh)]">
          <div className="space-y-2">
            {data.map(r => <RoutineRow key={r.id} routine={r} />)}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
